import { Fragment, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { ErrorBoundary } from './ErrorBoundary.js';
import type { Permission } from '@cms/shared';
import { useAuth } from '../lib/auth.js';
import { useHasPermission } from '../lib/permissions.js';
import { RecordTabBar } from './RecordTabBar.js';

/**
 * The shell, matching opioid-demo's layout: fixed dark sidebar, white sticky header with
 * the BlueLot logo, content area, footer with the tagline (docs/STYLING.md).
 *
 * Its sidebar shows every link and hides some behind a role check; here the nav is
 * permission-driven instead (§6.3 — permission strings, never role names), so adding a
 * role never means editing this file.
 *
 * The nav itself is grouped into collapsible sections — see `NAV` below.
 */

interface NavItem {
  to: string;
  label: string;
  icon: string;
  permission?: Permission;
  /** Every one of these, where one permission is not enough (the research export's record-level access). */
  allOf?: readonly Permission[];
  /** Any one of these (the funder reports: a Funder's permission, or staff's dashboard). */
  anyOf?: readonly Permission[];
}

interface NavGroup {
  /** Stable, because it keys the collapsed state and the `aria-controls` the header points at. */
  id: string;
  /**
   * The section header, or `null` for items that stand on their own.
   *
   * Dashboard has no group because it is not one of a kind of thing — it is where somebody lands, and
   * putting it under a heading would mean the first item in the sidebar could be collapsed out of sight.
   */
  label: string | null;
  items: NavItem[];
}

/**
 * The sidebar, in sections.
 *
 * ── Why grouped, and why the grouping is data ─────────────────────────────────────────
 * Sixteen links in one flat list is a list nobody reads: every item costs the same glance, so finding
 * Facilities means scanning past Measures and Cases. The sections say what kind of thing each link is —
 * records, the three ways data gets in, the two ways it comes out, and configuration — so somebody looks
 * in one place rather than at everything.
 *
 * It stays a data structure for the reason the flat list was one: permissions are strings, checked per
 * item, so adding a role never means editing this file (§6.3).
 */
const NAV: NavGroup[] = [
  {
    id: 'top',
    label: null,
    items: [
      {
        to: '/dashboard',
        label: 'Dashboard',
        icon: 'bi-speedometer2',
        permission: 'dashboard:read',
      },
    ],
  },
  {
    id: 'records',
    label: 'Records',
    items: [
      {
        to: '/participants',
        label: 'Participants',
        icon: 'bi-person-vcard',
        permission: 'participant:read',
      },
      {
        // Episodes. Context for the people events happen to, like Families — and deliberately not
        // where those events live (D3).
        to: '/cases',
        label: 'Cases',
        icon: 'bi-folder2-open',
        permission: 'case:read',
      },
      {
        // Households. A family is context for the people events happen to, not a thing that happens.
        to: '/families',
        label: 'Families',
        icon: 'bi-people',
        permission: 'family:read',
      },
      {
        // The recorded events themselves — overdoses, Narcan, Marchman/Baker, arrests.
        to: '/measures',
        label: 'Measures',
        icon: 'bi-clipboard-pulse',
        permission: 'measure:read',
      },
    ],
  },
  {
    /**
     * The three ways data gets in, minus the one that is not a page.
     *
     * A file, and a grid. Single-record forms are reached from the records themselves, which is why they
     * are not here — there is no "add a participant" nav item and never was.
     */
    id: 'entry',
    label: 'Data entry',
    items: [
      {
        // Where a report comes FROM, for the agencies that send a spreadsheet rather than one incident
        // at a time. An import produces reports and measures both.
        to: '/ingest',
        label: 'Imports',
        icon: 'bi-file-earmark-arrow-up',
        permission: 'ingest:read',
      },
      {
        /**
         * No permission, deliberately — unchanged from the flat list.
         *
         * The page offers only the kinds this account can both read and write, and says so honestly when
         * that is neither. Naming one entity's permission here would hide it from somebody who can
         * legitimately enter another.
         */
        to: '/grid',
        label: 'Grid entry',
        icon: 'bi-grid-3x3',
      },
    ],
  },
  {
    id: 'analysis',
    label: 'Reports & analysis',
    items: [
      {
        // The document a facility sent — the thing links and review items hang off.
        to: '/reports',
        label: 'Reports',
        icon: 'bi-file-earmark-text',
        permission: 'report:read',
      },
      {
        /**
         * No permission, for the same reason Grid entry has none.
         *
         * The workspace offers only the panels a user can read, so it is useful to anybody who can read
         * anything and empty-but-honest to anybody who cannot (QUERY-WORKSPACE-PLAN §6).
         */
        to: '/query',
        label: 'Query',
        icon: 'bi-search',
      },
      {
        // Reports kept to run again, the reader's own and their roles' (OCC-62).
        to: '/dashboard/saved-reports',
        label: 'Saved reports',
        icon: 'bi-bookmark',
        permission: 'dashboard:read',
      },
      {
        // Where each case's time standards stand, and what needs doing (slice 12).
        to: '/time-standards',
        label: 'Time standards',
        icon: 'bi-stopwatch',
        permission: 'case:read',
      },
      {
        // Aggregate reports for funders (slice 13): suppression always on.
        to: '/funder-reports',
        label: 'Funder reports',
        icon: 'bi-bar-chart-line',
        anyOf: ['funder:read', 'dashboard:read'],
      },
      {
        // The de-identified, linked dataset (slice 9): record-level access only.
        to: '/research-export',
        label: 'Research export',
        icon: 'bi-file-earmark-zip',
        allOf: ['measure:read', 'participant:read'],
      },
    ],
  },
  {
    id: 'admin',
    label: 'Admin',
    items: [
      {
        /*
         * Records that might be one person. Behind `participant:merge` — a permission in no role anybody
         * is given, so for almost every account this link does not exist.
         */
        to: '/duplicates',
        label: 'Duplicates',
        icon: 'bi-people-fill',
        permission: 'participant:merge',
      },
      {
        /*
         * People an import created for rows that identified nobody (PARTICIPANT-ID-PLAN D-8). Beside
         * Duplicates and behind the same permission, because it exists to feed merges.
         */
        to: '/unverified',
        label: 'Unverified People',
        icon: 'bi-person-exclamation',
        permission: 'participant:merge',
      },
      {
        /*
         * The choice lists people record against. `lookup:write`, not `user:write`: every role can READ
         * a vocabulary — no form renders without it — so the read permission says nothing about who may
         * change one.
         */
        to: '/admin/records',
        label: 'Record Management',
        icon: 'bi-list-check',
        permission: 'lookup:write',
      },
      {
        // What the forms start with (OCC-44): choices from the same lists, so the same permission.
        to: '/admin/entry-defaults',
        label: 'Data-entry defaults',
        icon: 'bi-input-cursor-text',
        permission: 'lookup:write',
      },
      {
        // The provisional definitions the dashboard's metrics follow — the county's to adjust.
        to: '/admin/definitions',
        label: 'Definitions',
        icon: 'bi-sliders',
        permission: 'definition:manage',
      },
      {
        // The windows each case's time standards are measured against (slice 12).
        to: '/admin/time-standards',
        label: 'Time standards',
        icon: 'bi-stopwatch',
        permission: 'definition:manage',
      },
      {
        /**
         * User management — accounts, password lifecycle, role assignment.
         *
         * It read "Admin" in the flat list, which inside a section headed Admin would have said Admin
         * twice and named neither. "User Management" rather than the page's own shorter heading, because
         * it sits beside Record Management and the pair reads as what it is: the two things an
         * administrator maintains. Nothing about the route or its permission changed.
         *
         * `user:write`, not `user:read` — Staff holds read and must not see the admin section.
         */
        to: '/admin/users',
        label: 'User Management',
        icon: 'bi-gear',
        permission: 'user:write',
      },
      { to: '/roles', label: 'Roles', icon: 'bi-shield-lock', permission: 'role:read' },
      {
        to: '/admin/change-log',
        label: 'Change log',
        icon: 'bi-clock-history',
        permission: 'audit:read',
      },
      {
        // Every email sent or only logged (slice 11).
        to: '/admin/outbox',
        label: 'Outbox',
        icon: 'bi-envelope',
        permission: 'audit:read',
      },
      {
        // The source reports arrive from — configuration the report flow depends on.
        to: '/facilities',
        label: 'Facilities',
        icon: 'bi-hospital',
        permission: 'facility:read',
      },
      { to: '/status', label: 'System Status', icon: 'bi-activity' },
    ],
  },
];

/**
 * Exported for its own test.
 *
 * The rules worth pinning are all in here — a header appears only when something under it is visible, the
 * permission gate is the same one `Can` applies, and collapsing hides links without removing them — and
 * reaching them through `AppShell` would mean standing up auth, tabs and a router to assert a list.
 */
/**
 * Which group holds the page somebody is looking at, if any.
 *
 * ── Why the longest match wins ────────────────────────────────────────────────────────
 * Routes nest. `/admin/users` and `/admin/records` are separate items, and a shorter sibling must not
 * claim a deeper path — so the item whose own path is longest is the one that answers, rather than
 * whichever happened to be listed first.
 *
 * A prefix match, not equality, because the pages people are actually on are records: sitting on
 * `/participants/6512…` is being in Records, and a sidebar that only recognised the list page would
 * collapse itself the moment somebody opened a record.
 */
function groupForPath(pathname: string): string | undefined {
  let best: { id: string; length: number } | undefined;

  for (const group of NAV) {
    for (const item of group.items) {
      if (pathname !== item.to && !pathname.startsWith(`${item.to}/`)) continue;
      if (best === undefined || item.to.length > best.length) {
        best = { id: group.id, length: item.to.length };
      }
    }
  }

  return best?.id;
}

export function NavItems() {
  const has = useHasPermission();
  const { pathname } = useLocation();

  /**
   * Closed on arrival, except the section holding the current page.
   *
   * Sixteen links in five sections is a lot to look at when the answer is one of them; closed by default
   * makes the sidebar a short list of places rather than a wall, and opening the one somebody is already
   * in means they can still see where they are.
   *
   * ── Why this is seeded once rather than recomputed per navigation ──────────────────────
   * Because after the first render the state belongs to the person, not the route. Recomputing it would
   * mean every navigation reopened one section and shut the others — including the one they had just
   * deliberately opened to go somewhere else. The shell stays mounted for the session, so a choice
   * survives navigating; a refresh starts again from where they are.
   */
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => {
    const current = groupForPath(pathname);
    return new Set(current === undefined ? [] : [current]);
  });

  function toggle(id: string): void {
    setExpanded((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }

  /**
   * The permission check that decides an ITEM also decides its HEADER.
   *
   * `useHasPermission` is exactly what `Can` reads, so this is the same gate applied one level up — a
   * section whose every item is hidden shows no heading, rather than an empty box implying there is
   * something there the account cannot reach.
   */
  /**
   * A Funder (slice 13) holds only `funder:read`: their account is the funder reports and nothing else, so the
   * items open to everyone (Grid entry, Query, the dashboard) are not offered to them either.
   */
  const funderOnly = has('funder:read') && !has('dashboard:read');
  const visible = (item: NavItem): boolean =>
    (!funderOnly || (item.anyOf ?? []).includes('funder:read')) &&
    (item.permission === undefined || has(item.permission)) &&
    (item.allOf ?? []).every((p) => has(p)) &&
    (item.anyOf === undefined || item.anyOf.some((p) => has(p)));

  return (
    <nav className="nav flex-column pb-3">
      {NAV.map((group) => {
        const items = group.items.filter(visible);
        if (items.length === 0) return null;

        const open = expanded.has(group.id);
        const links = items.map((item) => (
          <NavLink key={item.to} to={item.to} className="nav-link">
            <i className={`bi ${item.icon}`} aria-hidden="true" /> {item.label}
          </NavLink>
        ));

        if (group.label === null) return <Fragment key={group.id}>{links}</Fragment>;

        return (
          <div key={group.id} className="nav-group">
            <button
              type="button"
              className="nav-group-header"
              aria-expanded={open}
              aria-controls={`nav-group-${group.id}`}
              onClick={() => toggle(group.id)}
            >
              <span>{group.label}</span>
              <i
                className={`bi ${open ? 'bi-chevron-down' : 'bi-chevron-right'}`}
                aria-hidden="true"
              />
            </button>
            {/*
              `hidden` rather than removed from the tree, so collapsing a section does not throw away the
              links and rebuild them — and so a screen reader reading `aria-controls` finds the thing it
              names.
            */}
            <div id={`nav-group-${group.id}`} className="nav flex-column" hidden={!open}>
              {links}
            </div>
          </div>
        );
      })}
    </nav>
  );
}

export function AppShell() {
  const { user, logout } = useAuth();
  const has = useHasPermission();
  // A Funder has no dashboard (slice 13): the header does not offer one.
  const funderOnly = has('funder:read') && !has('dashboard:read');

  return (
    <>
      <aside className="sidebar print-hide">
        <div className="logo">BlueLot CMS</div>

        {user ? (
          <div className="user-info">
            <div className="d-flex align-items-center">
              <i className="bi bi-person-circle fs-3 me-2" aria-hidden="true" />
              <div>
                <div className="fw-bold text-white">
                  {user.firstName} {user.lastName}
                </div>
                <small>{user.roles.map((r) => r.name).join(', ') || 'No role'}</small>
              </div>
            </div>
          </div>
        ) : null}

        <NavItems />
      </aside>

      <div className="main-content">
        <header className="site-header print-hide">
          <a href="/" className="site-header-brand" aria-label="BlueLot home">
            <img src="/blue-lot-logo.png" alt="BlueLot" />
          </a>
          <div className="site-header-nav">
            {funderOnly ? null : <NavLink to="/dashboard">Dashboard</NavLink>}
            {user ? (
              <>
                <span className="site-header-user">
                  <i className="bi bi-person-circle me-1" aria-hidden="true" />
                  {user.firstName}
                </span>
                <button
                  type="button"
                  className="btn btn-link site-header-logout p-0 border-0 align-baseline"
                  onClick={() => void logout()}
                >
                  <i className="bi bi-box-arrow-right me-1" aria-hidden="true" />
                  Logout
                </button>
              </>
            ) : null}
          </div>
        </header>

        <RecordTabBar />

        {/*
          `page-content` carries `min-width: 0`, which is what lets a wide table scroll (see styles.css).
        */}
        <div className="p-4 flex-grow-1 page-content">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </div>

        <footer className="site-footer print-hide">
          <div className="site-footer-inner">
            <img src="/blue-lot-logo.png" alt="BlueLot" className="site-footer-logo" />
            <span>
              &copy; {new Date().getFullYear()} BlueLot &middot; A perfect spot for clear data
            </span>
          </div>
        </footer>
      </div>
    </>
  );
}
